import React from "react";
import "./NewTrainingRequest.scss";
import { useMutation, useQuery } from "react-query";
import trainingRequestsService from "../TrainingRequest/logic/useTrainingRequestService";
import { useEffect, useState, memo } from "react";
import http from "../../core/utils/apis/api";
import { formatDate } from "../../utils/dateFormatter";
import useAuth from "../../core/hooks/useAuth";
import { DatePicker, Select, Spin } from "antd";
import CustomAccordion from "./CustomAccordion";
import { makeDelay } from "../../core/utils/helpers";
import Pagination from "../../components/Pagination/Pagination";
import useDebounce from "../../core/hooks/useDebounce";

const NewTrainingRequest = () => {
  const [startDate, setStartDate] = useState(null);
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);


  const { authenticatedUser } = useAuth();
  const statusOptions = [
    { value: "pending", label: "pending" },
    { value: "accepted", label: "accepted" },
    { value: "rejected", label: "rejected" },
  ];
  const types = [
    { value: "inter", label: "inter" },
    { value: "intra", label: "intra" },
    { value: "customized", label: "customized" },
  ];

  const handleDateChange = (date, dateString) => {
    if (dateString === "" || date === null) {
      setStartDate(null);
    } else {
      const formattedDate = new Date(date).toISOString().split("T")[0];
      setStartDate(formattedDate);
    }
  };
  const handleTypeChange = (value) => {
    setType(value || "");
  };

  const handleStatusChange = (value) => {
    setStatus(value || "");
  };

  const handleSearchChange = (event) => {
    makeDelay(() => {
      if (event.target.value.length >= 2) {
        setSearchQuery(event.target.value);
        setPage(1);
      } else {
        setSearchQuery("");
      }
    }, 750);
  };

  const createFilterObject = (startDate, type, status) => {
    const filterObject = {};
    if (startDate) {
      filterObject.startDate = startDate;
    }

    if (type) {
      filterObject.type = type;
    }

    if (status) {
      filterObject.status = status;
    }

    return filterObject;
  };
  const handleNext = () => {
    setPage((page) => page + 1);
  };

  const handlePrevious = () => {
    setPage((page) => page - 1);
  };

  const { debouncedValue: searchDebouncedValue } = useDebounce(searchQuery, 1000);

  const {
    data: requests,
    isFetching,
    refetch: refetchTrainingRequest,
  } = useQuery(
    [
      "requests",
      createFilterObject(startDate, type, status),
      page,
      searchDebouncedValue,
    ],
    () =>
      trainingRequestsService.getAllRequestsData(
        createFilterObject(startDate, type, status),
        page,
        6,
        searchDebouncedValue,
      ),
    {
      initialData: {
        data: { requests: [], meta: { hasPrevious: false, hasNext: false } },
      },
      keepPreviousData: true,
      staleTime: 0,
    }
  );

  useEffect(() => {
    refetchTrainingRequest();
    setPage(1);
  }, [startDate, type, status, refetchTrainingRequest]);
  const  [progress, setProgress] = useState(0);
  const handleDownloadFile = (fileName) => {
    const config = {
      responseType: "blob",
      onDownloadProgress: (progressEvent) => {
        const loaded = progressEvent.loaded;
        const total = progressEvent.total;
        const downloadProgress = (loaded / total) * 100;
        setProgress(downloadProgress);
      },
    };

    http
      .get(`/requests/files/${fileName}`, config)
      .then((response) => {
        // Handle the file download as before
        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error("Error downloading the file:", error);
      });
  };
  const { mutate: updateRequestStatus } = useMutation(
    (requestId) =>
      trainingRequestsService.updateRequest(requestId, {
        status: "accepted",
        approvedBy: authenticatedUser?.id,
      }),
    {
      onSettled: () => {
        refetchTrainingRequest();
      },
    }
  );

  const handleAcceptRequest = (requestId) => {
    updateRequestStatus(requestId);
  };

  const { mutate: updateRequestStatusToRejected } = useMutation(
    (requestId) =>
      trainingRequestsService.updateRequest(requestId, {
        status: "rejected",
        approvedBy: authenticatedUser?.id,
      }),
    {
      onSettled: () => {
        refetchTrainingRequest();
      },
    }
  );

  const handleRejectRequest = (requestId) => {
    updateRequestStatusToRejected(requestId);
  };

  const modifiedData = requests?.data?.data?.map((request) => {
    const startDate = formatDate(request.proposed_start_date);
    const createdDate = formatDate(request.createdAt);
    const modifiedDate = formatDate(request.updatedAt);
    const type = request.type;

    return {
      id: request.id,
      headerLabels: [
        request.course?.title,
        request.status,
        startDate,
        request.user?.firstName + " " + request?.user?.lastName,
        type === "inter" ? "Avaxia" : request?.enterprise?.name,
      ],
      actionButtons: ["Accept", "Decline"],
      content: ["Request Comment", request?.comment],
      status: request.status,
      type: request.type,
      additionalInformation: {
        type: request.type,
        trainingMode: request.trainingMode,
        proposedPlanFile: request?.proposed_plan_file_path,
        location: request.location,
        jobTitle: request.user?.title,
        ProfAddress: request.enterprise?.address,
        country: request.enterprise?.country,
        phoneNumber: request.user?.telephone,
        participantNumber: request.nb_participants,
        created: createdDate,
        lastModified: modifiedDate,
        approvedBy: request.approver
          ? request.approver?.firstName + " " + request.approver?.lastName
          : "",
      },
    };
  });

  return (
    <div className="training-request">
      <div className="request-number">
        <label>
          Total Requests : {requests ? requests?.data?.meta?.itemCount : 0}
        </label>
      </div>
      <div className="request-search-training">
        <Select
          allowClear
          className="ant-select-selector"
          placeholder="Select status"
          options={statusOptions}
          onChange={(value) => {
            handleStatusChange(value);
          }}
        />
        <Select
          allowClear
          className="ant-select-selector"
          placeholder="Select type"
          options={types}
          onChange={(value) => {
            handleTypeChange(value);
          }}
        />
        <DatePicker className="date" onChange={handleDateChange} />
        <div className="search-container">
          <input
            className="search-input"
            type="search"
            placeholder="Search "
            onChange={handleSearchChange}
          />
        </div>
      </div>
      {requests?.data?.meta?.itemCount === 0 ? ( // Vérification de la condition
        <div className="no-data-message">No data</div> // Affichage du message si la condition est vraie
      ) : (



      <div className="accordion-container">
        <Spin spinning={isFetching} size="large">

        {modifiedData?.map((requestData, index) => (
          <CustomAccordion key={index}
          data={requestData}
          meta={requests?.data.meta}
          handleNext={handleNext}
          handlePrevious={handlePrevious}
          handleDownloadFile={handleDownloadFile}
          handleAcceptRequest={handleAcceptRequest}
          handleRejectRequest={handleRejectRequest}
          progress={progress}
          />
        ))}
        </Spin>
      </div>
      )}
      <Pagination
            hasNextPage={requests?.data?.meta.hasNextPage}
            hasPreviousPage={requests?.data?.meta.hasPreviousPage}
            handleNext={handleNext}
            handlePrevious={handlePrevious}
            currentPage={requests?.data?.meta?.page}
            totalPageCount={requests?.data?.meta?.pageCount}
      />
    </div>
  );
};

export default memo(NewTrainingRequest);
